"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import {
    Heading,
    Icon,
    InfoToolCard,
    Link,
} from "@farm-junction/design-library";
import Image from "next/image";
import { formatPrice, formatPriceRange } from "@/app/utilities/helper";
import { convertModelTags, findSpecValue } from "@/app/utilities/modelTagConverter";
import { getVariantList } from "@/app/utilities/apis";
import { useEmiData } from "@/hooks/useEmiData";
import { useRTOPricingData } from "@/hooks/useRTOPricingData";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import { customImageLoader } from "@/lib/utils/customLoader";
import LeadButton from "@/component/ui/Lead/LeadButton";
import MobileStickyCta, { MOBILE_STICKY_CTA_CLASS } from "@/component/pdp/MobileStickyCta";
import { PDP_EMICALCULATOR_POPUP_TRACKING } from "@/lib/constants/tracking";
import { buildCityPriceDescription, formatRupeeFull } from "./cityPriceDescription";
import PdpModelSummaryCard from "./PdpModelSummaryCard";

const EmiCalculator = dynamic(() => import("@/component/ui/EmiCalculator/EmiCalculator"));

const BRAND_COLOR = "#008C8B";
const OFFER_MONTH = new Date().toLocaleString("en-IN", { month: "long" });
const selectorCardClass =
    "rounded-[12px] border border-[#D8DEE8] bg-[#FAFAFA] px-4 py-[10px] [&_svg]:!h-[10px] [&_svg]:!w-[10px] [&_svg]:!min-h-[10px] [&_svg]:!min-w-[10px]";
const selectorHeadingClass =
    "!mb-0 !text-[11px] !leading-[14px] !font-normal !tracking-normal !text-[#8B93A0]";
const selectorValueClass = "!text-[14px] !font-semibold !leading-[18px] !text-[#101828]";
const selectorLinkClass =
    "!flex !w-full !items-center !justify-between !gap-2 !font-normal !text-inherit hover:!text-inherit";

const getPrefetchedVariants = (prefetchedVariantRes: any) => {
    if (Array.isArray(prefetchedVariantRes)) return prefetchedVariantRes;
    if (Array.isArray(prefetchedVariantRes?.data)) return prefetchedVariantRes.data;
    return [];
};

const compactPrice = (value: string) => {
    if (!value || /announced|request/i.test(value)) return value;
    return `${value.replace(/₹\s+/g, "₹").replace(/\s+-\s+/g, " – ").replace(/\*+$/, "")}*`;
};

const extractCcAndFuel = (variantItem: any) => {
    const rawFuel = variantItem?.fuel_types || findSpecValue(variantItem, "fuel_types") || "";
    const fuel = Array.isArray(rawFuel) ? rawFuel[0] || "" : String(rawFuel || "");
    const cc =
        variantItem?.engine ||
        variantItem?.engine_displacement ||
        findSpecValue(variantItem, "engine_displacement") ||
        "";
    return { cc: cc ? String(cc) : "", fuel };
};

type CityPriceMobileViewProps = {
    data: any;
    className?: string;
    brandLogoSrc?: string;
    cdnUrl?: string;
    prefetchedReviewRating?: number;
    prefetchedReviewCount?: number;
    prefetchedRtoResult?: any;
    prefetchedInsuranceResult?: any;
    prefetchedVariantId?: string | number;
    prefetchedCity?: string;
    prefetchedVariantRes?: any;
    EmiStartCardTracking?: string;
};

export default function CityPriceMobileView({
    data,
    className,
    brandLogoSrc = "",
    cdnUrl = process.env.NEXT_PUBLIC_CDN_URL || "",
    prefetchedReviewRating,
    prefetchedReviewCount,
    prefetchedRtoResult,
    prefetchedInsuranceResult,
    prefetchedVariantId,
    prefetchedCity,
    prefetchedVariantRes,
    EmiStartCardTracking,
}: CityPriceMobileViewProps) {
    const model = data?.modelData || data || {};
    const variant = Array.isArray(model?.base_variant) ? model.base_variant[0] : model?.base_variant;
    const prefetchedVariants = useMemo(() => getPrefetchedVariants(prefetchedVariantRes), [prefetchedVariantRes]);
    const router = useRouter();
    const variantPanelRef = useRef<HTMLDivElement>(null);
    const offerBtnRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);

    const [variants, setVariants] = useState<any[]>(
        prefetchedVariants.length > 0
            ? prefetchedVariants
            : Array.isArray(model?.variants)
              ? model.variants
              : variant
                ? [variant]
                : [],
    );
    const [selectedVariant, setSelectedVariant] = useState<any>(variant);
    const [showVariantPanel, setShowVariantPanel] = useState(false);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(true);

    const brandName = model?.brand_name || "";
    const modelName = model?.name || "";
    const brandSlug = model?.brand_slug || model?.brand?.slug || "";
    const modelSlug = model?.slug || "";
    const cityName = model?.locationInfo?.name || prefetchedCity || "";
    const rating = Number(prefetchedReviewRating ?? model?.user_rating ?? 0);
    const reviewCount = Number(prefetchedReviewCount ?? model?.reviews_count ?? 0);
    const isUpcoming = model?.status === "upcoming" || Boolean(model?.is_upcoming);
    const isDiscontinued = model?.status === "discontinued";
    const isElectric = Array.isArray(model?.fuel_types)
        ? model.fuel_types.some((type: string) => type?.toLowerCase() === "electric")
        : false;

    const rawProductImage = model?.images?.[0]?.url || model?.images?.[0] || model?.image || "";
    const productImageSrc = rawProductImage
        ? customImageLoader({
            src: /^https?:\/\//i.test(String(rawProductImage))
                ? String(rawProductImage)
                : `${cdnUrl}${String(rawProductImage).replace(/^\/+/, "")}`,
            width: 280,
            quality: 70,
        })
        : "";
    // const offerCtaLabel = `View ${OFFER_MONTH} Offers`;
    const offerCtaLabel = `View Diwali Offers`;
    const price = model?.price_range?.base || selectedVariant?.price || variant?.price || 0;
    const activeVariants = useMemo(
        () => variants.filter((item: any) => item?.status !== "discontinued"),
        [variants],
    );

    const { rtoResult, insuranceResult, selectedCity } = useRTOPricingData({
        model,
        selectedVariant,
        price,
        isPriceInCityRoute: true,
        prefetchedRtoResult,
        prefetchedInsuranceResult,
        prefetchedVariantId,
        prefetchedCity,
        syncCityFromCookieOnMount: true,
    });

    const {
        emiData,
        isEmiOpen,
        emiSelectedVariant,
        emiOnRoadPrices,
        handleOpenEmiCalculator,
        setIsEmiOpen,
        setEmiSelectedVariant,
    } = useEmiData({
        model,
        selectedVariant,
        variants,
        selectedCity,
        isPriceInCityRoute: true,
        rtoResult,
        insuranceResult,
        isRtoLoading: false,
        isInsuranceLoading: false,
        price,
    });

    useEffect(() => {
        if (!model?._id || prefetchedVariants.length > 0) return;
        let mounted = true;
        getVariantList(model._id, 1, 1000)
            .then((res: any) => {
                const list = res?.data || [];
                if (!mounted || !Array.isArray(list) || list.length === 0) return;
                setVariants(list);
                setSelectedVariant((current: any) => (current?._id ? current : list[0]));
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, [model?._id, prefetchedVariants.length]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (variantPanelRef.current && !variantPanelRef.current.contains(event.target as Node)) {
                setShowVariantPanel(false);
            }
        };
        if (showVariantPanel) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showVariantPanel]);

    const displayCity = selectedCity || cityName;

    const minPriceVariant = useMemo(() => {
        if (!Array.isArray(model?.variants) || model.variants.length === 0) return null;
        return model.variants.reduce((min: any, v: any) => {
            const vPrice = Number(v.price ?? 0);
            if (vPrice < (min.price ?? Infinity)) return v;
            return min;
        }, model.variants[0]);
    }, [model?.variants]);

    const minOnRoadPriceForTitle = useMemo(() => {
        if (!minPriceVariant || !model?.locationInfo) return 0;
        return getOnRoadPrice(minPriceVariant, model.locationInfo)?.onRoadPrice || 0;
    }, [minPriceVariant, model?.locationInfo]);

    const baseTitle = model?.h1_title
        ? convertModelTags(model.h1_title, model)
        : `${brandName} ${modelName}${
              model?.locationInfo?.name ? ` Price in ${model.locationInfo.name}` : cityName ? ` Price in ${cityName}` : " price"
          }`.trim();
    const pageTitle =
        minOnRoadPriceForTitle > 0 ? `${baseTitle} - Starts at ${formatPrice(minOnRoadPriceForTitle)}` : baseTitle;
    const description = useMemo(
        () =>
            buildCityPriceDescription({
                model,
                variants: variants.length ? variants : model.variants || [],
                cityName: displayCity || cityName,
                isUpcoming,
                isElectric,
            }),
        [model, variants, displayCity, cityName, isUpcoming, isElectric],
    );
    const { intro, sections, breakupTitle, breakupItems, compare, tableTitle, minOnRoad, maxOnRoad } = description;
    const variantPriceRows = useMemo(() => {
        if (!isDescriptionExpanded || !tableTitle) return [];
        return activeVariants
            .filter((item: any) => Number(item?.price) > 0)
            .sort((a: any, b: any) => Number(a.price) - Number(b.price))
            .map((item: any) => {
                const breakup = getOnRoadPrice(item, model?.locationInfo);
                const name = item?.name || "";
                const label =
                    modelName && name && !name.toLowerCase().includes(modelName.toLowerCase())
                        ? `${brandName} ${modelName} ${name}`.trim()
                        : name.startsWith(brandName)
                          ? name
                          : `${brandName} ${name}`.trim();
                return {
                    name: label,
                    exShowroom: formatRupeeFull(Number(item?.price)),
                    insurance: formatRupeeFull(breakup?.insuranceEstimate?.total) || "-",
                    rto: formatRupeeFull(breakup?.rtoEstimate?.total) || "-",
                    price: formatRupeeFull(breakup?.onRoadPrice),
                };
            })
            .filter((row: { name: string; price: string }) => row.name && row.price);
    }, [isDescriptionExpanded, tableTitle, activeVariants, model?.locationInfo, brandName, modelName]);

    useLayoutEffect(() => {
        const el = descriptionRef.current;
        if (!el || isDescriptionExpanded) return;
        setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [intro, isDescriptionExpanded]);
    const onRoadPriceRange = compactPrice(
        minOnRoad
            ? formatPriceRange({ base: minOnRoad, top: maxOnRoad || minOnRoad })
            : formatPriceRange(model?.price_range),
    );

    const exShowroom = Number(selectedVariant?.price || price) || 0;
    const rtoTotal = Number(rtoResult?.total) || 0;
    const insuranceTotal = Number(insuranceResult?.total) || 0;
    const totalOnRoad = exShowroom + rtoTotal + insuranceTotal;
    const emiAmount = emiData?.amount ? `₹${emiData.amount}` : "";
    const selectedVariantName = selectedVariant?.name?.trim() || "Base";

    const priceRows = isUpcoming
        ? [
              {
                  label: "Estimated Ex-Showroom Price",
                  value: exShowroom
                      ? formatRupeeFull(exShowroom)
                      : isUpcoming
                        ? "Price to be Announced"
                        : "Price on request",
              },
              {
                  label: "Estimated Other Charges",
                  value:
                      rtoResult || insuranceResult
                          ? formatRupeeFull(rtoTotal + insuranceTotal)
                          : "",
              },
          ]
        : [
              {
                  label: "Ex-Showroom Price",
                  value: exShowroom ? formatRupeeFull(exShowroom) : "Price on request",
              },
              {
                  label: "RTO, Registration",
                  value: rtoResult ? formatRupeeFull(rtoTotal) : "",
              },
              { label: "Insurance", value: insuranceResult ? formatRupeeFull(insuranceTotal) : "" },
          ];

    const saveSelVariantCookie = (nextModelSlug: string, nextVariantSlug: string) => {
        if (!nextModelSlug || !nextVariantSlug) return;
        document.cookie = `_selVariant=${encodeURIComponent(`${nextModelSlug}:${nextVariantSlug}`)};path=/;max-age=${60 * 60 * 24 * 30}`;
    };

    const handleSelectVariant = (nextVariant: any) => {
        setSelectedVariant(nextVariant);
        setShowVariantPanel(false);
        saveSelVariantCookie(modelSlug, nextVariant?.slug);
        router.refresh();
    };

    const handleOpenLocation = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("open-location-modal"));
    };

    const offerButton = (fullWidth = false, label = offerCtaLabel) => (
        <LeadButton
            carId={model?._id}
            variantId={selectedVariant?._id || model?.variant_id}
            carName={pageTitle}
            btnText={isUpcoming ? "Notify Me" : label}
            useSplashPopup
            is_upcoming={isUpcoming}
            formHeading={
                <>
                    Get on road Price for{" "}
                    <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                        {brandName} {modelName}
                    </span>{" "}
                    in{" "}
                    <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                        {displayCity}
                    </span>
                </>
            }
            className={
                fullWidth
                    ? "flex h-12 w-full items-center justify-center rounded-[8px] border-0 bg-[#008C8B] text-[14px] font-medium leading-none text-white hover:bg-[#007A79]"
                    : "flex shrink-0 items-center justify-center rounded-[8px] border-0 bg-[#008C8B] px-3 py-3 text-[14px] font-medium leading-none text-white hover:bg-[#007A79]"
            }
        />
    );

    return (
        <section data-brand="car-junction" className={className} data-testid="city-price-mobile-view">
            <div className="mb-3 flex flex-col gap-2">
                <Heading
                    variant="h1"
                    level={1}
                    color="black"
                    fontWeight="bold"
                    className="!mb-1 !text-[20px] !leading-[28px] !tracking-normal !text-[#101828]"
                >
                    {pageTitle}
                </Heading>                
                {(rating > 0 || reviewCount > 0) && (
                    <div className="flex items-center gap-2 shrink-0">
                        {rating > 0 && (
                            <p className="text-[13px] leading-none">
                                <span className="font-bold text-black">{rating.toFixed(1)}</span>
                                <span className="font-normal text-[#5F6B7A]"> /</span>
                                <span className="text-[11px] font-normal text-[#5F6B7A]"> 5</span>
                            </p>
                        )}
                        {rating > 0 && (
                            <Image
                                src="https://static-asset.tractorjunction.com/cj/yellow-star.svg"
                                alt="Rating"
                                title="Rating"
                                width={12}
                                height={12}
                                className="size-3"
                            />
                        )}
                        {reviewCount > 0 && (
                            <a href="#customer-reviews" className="text-[13px] font-normal text-[#155DFC] hover:underline">
                                {reviewCount} Reviews
                            </a>
                        )}
                    </div>
                )}
            </div>

            {intro && (
                <div className="mb-4 text-[15px] leading-5 text-[#6B7280]">
                    <div ref={descriptionRef} className={isDescriptionExpanded ? "" : "line-clamp-2 overflow-hidden"}>
                        <p>{intro}</p>
                    </div>
                    {isDescriptionExpanded && (
                        <div className="mt-3 space-y-3">
                            {sections.map((section) => (
                                <div key={section.title}>
                                    <p className="font-semibold text-[#101828]">{section.title}</p>
                                    <ul className="mt-1.5 list-disc space-y-1 pl-5">
                                        {section.items.map((item) => (
                                            <li key={item.label}>
                                                <strong className="font-semibold text-[#101828]">{item.label}:</strong> {item.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            {breakupItems.length > 0 && (
                                <div>
                                    <p className="font-semibold text-[#101828]">{breakupTitle}</p>
                                    <ul className="mt-1.5 list-disc space-y-1 pl-5">
                                        {breakupItems.map((item) => (
                                            <li key={item.label}>
                                                <strong className="font-semibold text-[#101828]">{item.label}:</strong> {item.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {compare && <p>{compare}</p>}
                            {variantPriceRows.length > 0 && (
                                <div>
                                    <p className="mb-2 font-semibold text-[#101828]">{tableTitle}</p>
                                    <div
                                        className={`overflow-x-auto rounded-xl border border-[#E8ECF0]${variantPriceRows.length > 6 ? " max-h-[240px] overflow-y-auto" : ""}`}
                                    >
                                        <table className="w-full min-w-[600px] border-collapse">
                                            <thead className="sticky top-0 bg-[#F9F9F9]">
                                                <tr>
                                                    <th className="sticky left-0 bg-[#F9F9F9] px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-[#8B93A0]">
                                                        Variants
                                                    </th>
                                                    {["Ex-Showroom Price", "Insurance", "RTO", "On-Road Price"].map((heading) => (
                                                        <th
                                                            key={heading}
                                                            className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.04em] text-[#8B93A0]"
                                                        >
                                                            {heading}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {variantPriceRows.map((row) => (
                                                    <tr key={row.name} className="border-t border-[#E8ECF0]">
                                                        <td className="sticky left-0 bg-white px-3 py-2.5 text-left text-[13px] font-medium text-[#101828]">{row.name}</td>
                                                        {[row.exShowroom, row.insurance, row.rto, row.price].map((value, idx) => (
                                                            <td key={idx} className="whitespace-nowrap px-3 py-2.5 text-right text-[13px] font-medium text-[#101828]">{value}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {(isDescriptionOverflowing || isDescriptionExpanded) && (
                        <button
                            type="button"
                            className="mt-2 block p-0 text-[13px] font-medium leading-5 text-[#007AFF] cursor-pointer"
                            onClick={() => setIsDescriptionExpanded((open) => !open)}
                        >
                            {isDescriptionExpanded ? "Read Less" : "Read More"}
                        </button>
                    )}
                </div>
            )}

            <PdpModelSummaryCard
                brandName={brandName}
                modelName={modelName}
                href={brandSlug && modelSlug ? `/${brandSlug}/${modelSlug}` : "#"}
                productImageSrc={productImageSrc}
                rating={rating}
                leftLabel={`On-road price · ${displayCity}`}
                leftValue={onRoadPriceRange}
                rightLabel="EMI starts at"
                rightValue={emiAmount || "—"}
                footer={!isDiscontinued ? offerButton(true) : undefined}
                footerRef={offerBtnRef}
            />

            <Heading
                variant="h2"
                level={2}
                color="black"
                fontWeight="bold"
                className="mt-6 !mb-4 !text-[20px] !leading-[26px] !tracking-normal !text-[#101828]"
            >
                {`${brandName} ${modelName} On Road Price in ${displayCity}`.trim()}
            </Heading>

            <div className="relative grid grid-cols-2 gap-2.5" ref={variantPanelRef}>
                <InfoToolCard
                    title={`Variants (${activeVariants.length})`}
                    description={selectedVariantName}
                    href="#variants"
                    showImage={false}
                    className={selectorCardClass}
                    slotProps={{
                        headingProps: { variant: "p", level: "p", color: "tertiary", className: selectorHeadingClass },
                        descriptionProps: { isTruncated: false, className: selectorValueClass },
                        linkProps: {
                            title: selectedVariantName,
                            className: selectorLinkClass,
                            onClick: (event) => {
                                event.preventDefault();
                                setShowVariantPanel((open) => !open);
                            },
                        },
                    }}
                />
                <InfoToolCard
                    title="Location"
                    description={displayCity || "Select city"}
                    href="#location"
                    showImage={false}
                    className={selectorCardClass}
                    slotProps={{
                        headingProps: { variant: "p", level: "p", color: "tertiary", className: selectorHeadingClass },
                        descriptionProps: { isTruncated: false, className: selectorValueClass },
                        linkProps: {
                            title: displayCity,
                            className: selectorLinkClass,
                            onClick: handleOpenLocation,
                        },
                    }}
                />

                {showVariantPanel && activeVariants.length > 0 && (
                    <div className="absolute top-full left-0 z-50 mt-2 max-h-[320px] w-full overflow-y-auto rounded-xl border border-[#E8ECF0] bg-white shadow-lg">
                        {activeVariants.map((item: any) => {
                            const isSelected = selectedVariant?._id === item._id || selectedVariant?.name === item.name;
                            const info = extractCcAndFuel(item);
                            const ccFuelLabel = `${info.cc}${info.cc && info.fuel ? " • " : ""}${info.fuel}`;
                            return (
                                <button
                                    key={item._id || item.slug || item.name}
                                    type="button"
                                    onClick={() => handleSelectVariant(item)}
                                    className={`flex w-full items-start gap-2 border-b border-[#E8ECF0] px-3 py-2.5 text-left last:border-b-0 ${isSelected ? "bg-[#008C8B0D]" : "bg-white"}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate text-[13px] font-medium text-[#101828]">
                                                {item.name}
                                            </span>
                                            {item?.is_top_variant && (
                                                <span className="shrink-0 rounded bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">
                                                    Top
                                                </span>
                                            )}
                                            {item?.is_base_variant && (
                                                <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700">
                                                    Base
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 flex items-center justify-between gap-2">
                                            {ccFuelLabel ? (
                                                <span className="text-[12px] text-[#6B7280]">{ccFuelLabel}</span>
                                            ) : (
                                                <span />
                                            )}
                                            {item.price ? (
                                                <span className="shrink-0 text-[12px] text-[#6B7280]">
                                                    {formatPrice(Number(item.price))}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <p className="mt-4 mb-2 text-[13px] font-bold leading-[17px] text-[#101828]">Price Summary</p>
            <table className="w-full border-collapse">
                <tbody>
                    {priceRows.map((row) => (
                        <tr key={row.label}>
                            <td className="border-0 border-b border-solid border-[#E8ECF0] py-3.5 text-left text-[14px] leading-5 font-normal text-[#4B5563]">{row.label}</td>
                            <td className="border-0 border-b border-solid border-[#E8ECF0] py-3.5 text-right text-[14px] leading-5 font-medium text-[#101828]">{row.value}</td>
                        </tr>
                    ))}
                    <tr>
                        <td className="border-0 pt-3.5 pb-3.5 text-left text-[15px] leading-5 font-semibold text-[#101828]">
                            {isUpcoming ? "Estimated On-Road Price" : "Total On-Road Price"}
                        </td>
                        <td className="border-0 pt-3.5 pb-3.5 text-right text-[15px] leading-5 font-bold text-[#008C8B]">
                            {rtoResult || insuranceResult ? formatRupeeFull(totalOnRoad) : ""}
                        </td>
                    </tr>
                </tbody>
            </table>

            {!isDiscontinued && (
                <div
                    className="mt-4 flex items-center justify-between gap-3 rounded-[8px] bg-[#FAFAFA] px-3 py-2.5"
                    style={{ border: "1px solid #D8DEE826" }}
                >
                    <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-[14px] font-bold leading-5 text-[#101828]">
                            EMI {emiAmount ? `${emiAmount}/month` : ""}
                            {emiData?.tooltip ? (
                                <span className="group relative inline-flex shrink-0 items-center" tabIndex={0}>
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}search-icons/exclamatory-white.svg`}
                                        alt=""
                                        width={14}
                                        height={14}
                                        className="cursor-pointer rounded-full border border-[#545454]"
                                        aria-hidden
                                    />
                                    <span className="sr-only">{emiData.tooltip}</span>
                                    <span className="invisible absolute bottom-full left-1/2 z-10 mb-2 w-56 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-[11px] font-normal leading-4 text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                                        {emiData.tooltip}
                                    </span>
                                </span>
                            ) : null}
                        </p>
                        <Link
                            href={`/${brandSlug}/${modelSlug}/car-loan-emi-calculator`}
                            title="EMI Calculator"
                            className={`!mt-0.5 !p-0 !text-[13px] !font-medium !leading-4 ${EmiStartCardTracking || ""}`}
                            style={{ color: BRAND_COLOR }}
                            onClick={(event) => {
                                event.preventDefault();
                                handleOpenEmiCalculator();
                            }}
                        >
                            EMI Calculator
                        </Link>
                    </div>
                    {offerButton(false, "View EMI Offers")}
                </div>
            )}

            {isEmiOpen && (
                <EmiCalculator
                    carName={`${brandName} ${modelName}`}
                    variants={variants}
                    onRoadPrices={
                        Object.keys(emiOnRoadPrices).length > 0 || (selectedVariant?._id && rtoResult && insuranceResult)
                            ? {
                                  ...emiOnRoadPrices,
                                  ...(selectedVariant?._id && rtoResult && insuranceResult
                                      ? {
                                            [selectedVariant._id]:
                                                (selectedVariant?.price || price) + rtoResult.total + insuranceResult.total,
                                        }
                                      : {}),
                              }
                            : undefined
                    }
                    cityName={displayCity}
                    isMobile
                    selectedVariantId={emiSelectedVariant?._id}
                    onVariantChange={(nextVariant) => setEmiSelectedVariant(nextVariant)}
                    isOpen={isEmiOpen}
                    onClose={() => setIsEmiOpen(false)}
                    VarentSectorClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_VARIANT_SELECTOR_CLICK}
                    DownPaymentClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_DOWN_PAYMENT_CLICK}
                    LoanTermClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_LOAN_PERIOD_CLICK}
                    InterestRateClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_INTEREST_RATE_CLICK}
                />
            )}
            {!isDiscontinued && (
                <MobileStickyCta anchorRef={offerBtnRef} reserveSpace>
                    <LeadButton
                        carId={model?._id}
                        variantId={selectedVariant?._id || model?.variant_id}
                        carName={pageTitle}
                        btnText={isUpcoming ? "Notify Me" : offerCtaLabel}
                        useSplashPopup
                        is_upcoming={isUpcoming}
                        formHeading={
                            <>
                                Get on road Price for{" "}
                                <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                                    {brandName} {modelName}
                                </span>{" "}
                                in{" "}
                                <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                                    {displayCity}
                                </span>
                            </>
                        }
                        className={MOBILE_STICKY_CTA_CLASS}
                    />
                </MobileStickyCta>
            )}
        </section>
    );
}
